import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { accountAccessShowLogin } from 'actions/UiActions'
import {
  set as setLocalStorage,
  get as getLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import moment from 'moment'

const LOCAL_STORAGE_TIMER_NAME = 'loginModalExpirationTime'
const LOGIN_MODAL_EXPIRATION_TIME_IN_MINUTES = 60
const LOGIN_MODAL_DELAY_IN_SECONDS = 30

function useShowLogin() {
  const [scrolled, setScrolled] = useState(false)
  const [timerExpired, setTimerExpired] = useState<boolean>()
  const isLoggedIn = useAppSelector(isLoggedInSelector)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isLoggedIn || scrolled) {
      return
    }

    const handleScroll = () => {
      setScrolled(true)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoggedIn, scrolled])

  const { value: isDelayed, off: finishDelay } = useToggle(true)
  useEffect(() => {
    let timeout
    if (!isLoggedIn && scrolled) {
      timeout = setTimeout(finishDelay, LOGIN_MODAL_DELAY_IN_SECONDS * 1000)
    }
    return () => clearTimeout(timeout)
  }, [finishDelay, isLoggedIn, scrolled])

  // To prevent the login modal popping up straight away for users who are sent to a new tab, we set a time in their local storage and only setTimerExpired if it's been more than an hour
  useEffect(() => {
    const timerExpiration = getLocalStorage(LOCAL_STORAGE_TIMER_NAME) ?? undefined
    const now = new Date().getTime()

    if (now >= timerExpiration) {
      setTimerExpired(true)
    } else {
      const timeoutId = setTimeout(() => {
        setTimerExpired(true)
      }, timerExpiration - now)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  useEffect(() => {
    if (!isLoggedIn && !isDelayed && timerExpired) {
      dispatch(accountAccessShowLogin())
      setLocalStorage(LOCAL_STORAGE_TIMER_NAME, moment().add(LOGIN_MODAL_EXPIRATION_TIME_IN_MINUTES, 'minutes').toDate().getTime().toString())
    }
  }, [dispatch, isDelayed, isLoggedIn, timerExpired])
}

export default useShowLogin
